import { useState } from "react";
import { Typography } from "@mui/material";
import { useController, useFormState } from "react-hook-form";
import { EntrySelector } from "./EntrySelector";
import { T } from "../../util/t";
import { makeStyles } from "../../providers/makeStyles";
import { AddAthleteButton } from "./AddAthleteButton";
import { FieldMessage } from "../forms/FormField";
import { AthleteSelector } from "./AthleteSelector";
import { SelectedAthletes } from "./SelectedAthletes";
import { MembershipRequiredNotification } from "./MembershipRequired";

export const SELECTORS = {
    "athlete": AthleteSelector,
    "entry": EntrySelector
};

const useStyles = makeStyles(theme => ({
    section: {
        marginTop: theme.spacing(2)
    },
    accordionRoot: {
        border: `2px solid ${theme.palette.border.main}`,
        borderRadius: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    accordionExpanded: {
        "&&": {
            "&:last-of-type": {
                marginBottom: theme.spacing(1)
            }
        }
    }
}));
export const RegisteringAthletes = ({ step, control, getValues, trigger, resetField }) => {
    const { field: input } = useController({
        control,
        name: "athletes",
        rules: {
            validate: value => {
                if (value.length < 1) {
                    if (step.isSeriesRegistration) {
                        return "You'll need to register at least one athlete.";
                    }
                    return "You'll need to add at least one entry.";
                }
            }
        },
    });

    const [expanded, setExpanded] = useState(input.value.length === 1 ? 0 : false);
    const { athletes, options, registrationOptions, captureFields, paymentOptions, exclusiveSeries, isSeriesRegistration, organisation } = step;

    const validate = () => trigger([`athletes.${expanded}`], { shouldFocus: true });
    const handleChange = panel => (event, isExpanded) =>
        validate().then(valid => valid && setExpanded(isExpanded ? panel : false));

    const removeAthlete = index => {
        const currentValue = getValues("athletes");
        currentValue.splice(index, 1);
        input.onChange(currentValue);
    };

    const appendAthlete = (athlete, index) => {
        const currentValue = getValues("athletes");
        if (!currentValue.length) resetField("athletes", { defaultValue: [] });
        currentValue.push(athlete);
        input.onChange(currentValue);
        setExpanded(index ?? currentValue.length - 1);
    };

    const updateAthlete = (index, athlete) => {
        const currentValue = [...getValues("athletes")];
        currentValue.splice(index, 1, athlete);
        input.onChange(currentValue);
    };

    const selectAthlete = athlete => {
        const index = input.value.findIndex(a => (a.id && a.id === athlete.id) || a.name === athlete.name);
        handleChange(index)(null, true);
    };
    const classes = useStyles();

    const Selector = SELECTORS[isSeriesRegistration ? "athlete" : "entry"];
    const teamName = getValues("team.name");

    const membershipPurchaseRequired = athletes.length === 0 && exclusiveSeries.length > 0;

    return (
        <>
            <Typography variant="h7" component="h2">
                {isSeriesRegistration
                    ? <T>Register athletes</T>
                    : teamName ? <T team={teamName}>entries_for_team</T> : <T>Entries</T>
                }
            </Typography>

            {membershipPurchaseRequired && <div className={classes.section}>
                <MembershipRequiredNotification exclusiveSeries={exclusiveSeries} organisation={organisation}>
                    exclusive_registration_notification
                </MembershipRequiredNotification>
            </div>}

            <div className={classes.section}>
                <SelectedAthletes
                    athletes={athletes}
                    expanded={expanded}
                    handleChange={handleChange}
                    removeAthlete={removeAthlete}
                    updateAthlete={updateAthlete}
                    appendAthlete={appendAthlete}
                    control={control}
                    getValues={getValues}
                    options={options}
                    registrationOptions={registrationOptions}
                    captureFields={captureFields}
                    paymentOptions={paymentOptions}
                    isSeriesRegistration={isSeriesRegistration}
                />

                {membershipPurchaseRequired
                    ? <AddAthleteButton numberOfAthletes={input.value.length} disabled/>
                    : <Selector
                        athletes={athletes}
                        options={options}
                        registrationOptions={registrationOptions}
                        paymentOptions={paymentOptions}
                        appendAthlete={appendAthlete}
                        selectAthlete={selectAthlete}
                        control={control}
                        exclusiveSeries={exclusiveSeries}
                        organisation={organisation}
                        checkAthleteSelectorShouldOpen={validate}
                        isSeriesRegistration={isSeriesRegistration}
                    />}
            </div>

            <NoAthletesError control={control} />
        </>
    );
};

const NoAthletesError = ({ control }) => {
    const { errors } = useFormState({ control });

    return <FieldMessage errorMessage={errors?.athletes?.message} />;
};
